/**
 * AiAssistant.js — Main coordinator for the AI assistant.
 * Wires together: panel, chips, context, intent detection and API calls.
 *
 * Usage (in index.js, after initModeler):
 *   import { AiAssistant } from './ai/AiAssistant.js';
 *   const ai = new AiAssistant();
 *   ai.mount();
 */

import './ai-panel.css';
import { AiPanel }                          from './AiPanel.js';
import { AiVoice }                          from './AiVoice.js';
import { getChips, getIntentForChip, isNativeIntent } from './AiChips.js';
import { buildContext, buildDmnContext }    from './AiContext.js';
import { detectIntent }                     from './AiIntent.js';
import { assist, transcribe, describe, generateBpmn, extractDocument, suggestOutline } from './aiApi.js';
import { parseOutline, serializeOutline, validateOutline, renderOutlineHtml } from './AiOutline.js';
import { renderProseHtml, validateProse }   from './AiProse.js';
import { DmnGenerateModal }                 from './DmnGenerateModal.js';

// Panel states
const STATE = { IDLE: 'idle', LOADING: 'loading', WAITING_CONFIRM: 'waiting_confirm' };

// WT-G — known structured DMN actions[] the FE can apply natively
// (THEMIS_DMN_PLAN §4.4 + THEMIS_ML_DECISION_PLAN §5.4 + THEMIS_CHAT_PLAN §3.5).
// Anything not in this set is logged with a warning and dropped — never crashes
// the chat.
const DMN_STRUCTURED_ACTIONS = new Set([
  // §4.4 catalogue
  'rename', 'set_description', 'set_type',
  'add_rule', 'add_input_column', 'add_output_column', 'set_hit_policy',
  'set_feel', 'add_context_entry', 'add_list_item',
  'add_relation_row', 'add_relation_column',
  'set_function_params', 'link_document',
  // Themis "Sugerir inputs faltantes" — creates real InputData nodes wired to
  // the focused decision (rather than just appending to its description).
  'add_input_data',
  // ML extensions
  'set_ml_model',
  'add_ml_input_binding', 'remove_ml_input_binding',
  'add_ml_output_binding', 'remove_ml_output_binding',
  'set_ml_fallback', 'set_ml_threshold',
  // THEMIS_CHAT_PLAN §3.5 — simulator + navigation actions
  'load_scenarios', 'run_scenarios',
  'open_decision', 'highlight_decision', 'highlight_rules',
  'suggest_rule',
]);

function _summarizeDecisionForWelcome(decisionLogic) {
  if (!decisionLogic || !decisionLogic.$type) return '';
  const $type = decisionLogic.$type;
  if ($type === 'epx:MachineLearningDecision') return 'decisión ML';
  if ($type.endsWith(':DecisionTable')) {
    const rules   = Array.isArray(decisionLogic.rule)   ? decisionLogic.rule.length   : 0;
    const inputs  = Array.isArray(decisionLogic.input)  ? decisionLogic.input.length  : 0;
    return `tabla de decisión con ${rules} regla(s) y ${inputs} input(s)`;
  }
  if ($type.endsWith(':LiteralExpression'))   return 'expresión literal';
  if ($type.endsWith(':Context'))             return 'contexto FEEL';
  if ($type.endsWith(':List'))                return 'lista FEEL';
  if ($type.endsWith(':Invocation'))          return 'invocación de BKM';
  if ($type.endsWith(':Relation'))            return 'relación';
  if ($type.endsWith(':FunctionDefinition'))  return 'función';
  return '';
}

export class AiAssistant {
  constructor() {
    this._panel           = new AiPanel();
    this._voice           = new AiVoice();
    this._selection       = [];
    this._abortController = null;
    this._state           = STATE.IDLE;
    this._pendingDesc     = null;  // { description, language } while WAITING_CONFIRM
    this._generatedXml    = null;  // BPMN XML from generate-bpmn, held until Apply
    this._mode            = 'bpmn'; // 'bpmn' | 'rules'
    this._decisionRef     = null;   // active BusinessRuleTask decisionRef in rules mode
    this._boundFlow       = null;   // the epxFlow instance currently wired for selection
    this._lastChip        = null;   // chip label that triggered the last suggest_options request
    this._uploadedDocs    = [];     // [{ name, text, sizeKb, pages, charCount }] for /describe
    this._currentOutline  = '';     // editable text shown in preview (NaLa2BPMN prose in Camino 2)
    this._outlineEditing  = false;  // true while user is editing inline
    this._currentActors   = [];     // detected_elements.actors from /describe — fed to renderer for highlighting
    this._inputFormat     = 'prose';// 'prose' (Camino 2 — DSPy) | 'outline' (legacy fast-path)
    // PR 4 §4.3 — DMN wizard ("Generar reglas"). Lazy-mounted on first use so
    // the BPMN-only path stays untouched on initial paint.
    this._dmnGenerateModal = null;
  }

  mount() {
    // ── FAB → open panel ────────────────────────────────────────────────
    document.getElementById('ai-fab')
      ?.addEventListener('click', () => this._panel.open());

    // ── Close button ─────────────────────────────────────────────────────
    document.getElementById('ai-btn-close')
      ?.addEventListener('click', () => {
        this._cancelInFlight();
        this._panel.close();
      });

    // ── Generate modal wiring ─────────────────────────────────────────────
    this._mountGenerateModal();

    // ── Voice button in main panel input ─────────────────────────────────
    this._mountVoiceButton('ai-voice-btn', document.getElementById('ai-input'));

    // ── Selection changes → wired via attachToFlow (supports re-init) ───────
    if (window.epxFlow) this.attachToFlow(window.epxFlow);

    // ── Send button ──────────────────────────────────────────────────────
    document.getElementById('ai-send')
      ?.addEventListener('click', () => this._send());

    // ── Enter key in textarea (Shift+Enter = newline) ────────────────────
    document.getElementById('ai-input')
      ?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this._send();
        }
      });

    // ── Enable/disable send button as user types ─────────────────────────
    document.getElementById('ai-input')
      ?.addEventListener('input', () => {
        const hasText = document.getElementById('ai-input').value.trim().length > 0;
        document.getElementById('ai-send').disabled = !hasText;
      });

    // ── Initial state ────────────────────────────────────────────────────
    this._panel.setChips(getChips([]), (chip) => this._onChip(chip));

    // Restore history from previous session
    const history = this._panel.loadHistory();
    if (history.length > 0) {
      for (const msg of history) {
        if (msg.role === 'user') this._panel.addUserMessage(msg.content);
        else this._panel.addAiMessage(msg.content);
      }
    }
  }

  // ── Flow attachment (called on init and on re-init after locale change) ──────

  /**
   * Registers the selection listener on a new epxFlow instance.
   * Safe to call multiple times — guards against stale instances.
   * @param {object} flow  EpxFlow instance
   */
  attachToFlow(flow) {
    if (!flow) return;
    this._boundFlow = flow;

    flow.on('selection.changed', ({ newSelection }) => {
      // Drop stale events from a destroyed instance
      if (this._boundFlow !== flow) return;
      // In rules mode the DMN panel is active — ignore BPMN canvas clicks
      if (this._mode === 'rules') return;

      this._selection = newSelection || [];
      this._applySelectionMode();
    });
  }

  _applySelectionMode() {
    const sel = this._selection;
    this._panel.setContext(sel);
    if (sel.length === 0) {
      this._panel.setVisualMode('none');
    } else {
      this._panel.setVisualMode('selection');
    }
    this._panel.setChips(getChips(sel, 'bpmn'), (chip) => this._onChip(chip));
  }

  // ── Voice button helper ─────────────────────────────────────────────────────

  _mountVoiceButton(btnId, targetTextarea) {
    const btn = document.getElementById(btnId);
    if (!btn || !targetTextarea) return;

    if (!this._voice.isSupported()) {
      btn.disabled = true;
      btn.title = 'Tu navegador no soporta grabación de voz';
      return;
    }

    btn.addEventListener('click', async () => {
      if (this._voice.isRecording()) {
        this._voice.stopRecording();
        btn.classList.remove('is-recording');
        targetTextarea.classList.remove('is-listening');
        btn.title = 'Grabar voz';
        return;
      }

      btn.classList.add('is-recording');
      targetTextarea.classList.add('is-listening');
      btn.title = 'Detener grabación';

      try {
        await this._voice.startRecording(async (blob) => {
          btn.classList.remove('is-recording');
          targetTextarea.classList.remove('is-listening');
          btn.classList.add('is-transcribing');
          btn.title = 'Transcribiendo…';
          btn.disabled = true;

          const loadingEl = this._panel.addLoadingBubble('default', 'Transcribiendo…');
          try {
            const text = await transcribe(blob);
            this._panel.replaceLoading(loadingEl, null);
            targetTextarea.value = text;
            targetTextarea.focus();
            // Enable send button
            const sendBtn = document.getElementById(
              targetTextarea.id === 'ai-input' ? 'ai-send' : 'ai-gen-submit'
            );
            if (sendBtn) sendBtn.disabled = text.trim().length === 0;
          } catch (err) {
            this._panel.replaceLoading(loadingEl, null);
            this._panel.addErrorBubble(`Transcripción fallida: ${err.message}`, null);
          } finally {
            btn.classList.remove('is-transcribing');
            btn.title = 'Grabar voz';
            btn.disabled = false;
          }
        });
      } catch (err) {
        btn.classList.remove('is-recording');
        targetTextarea.classList.remove('is-listening');
        btn.title = 'Grabar voz';
        this._panel.addErrorBubble(`Micrófono no disponible: ${err.message}`, null);
      }
    });
  }

  // ── Generate modal — wizard ─────────────────────────────────────────────────

  _mountGenerateModal() {
    const modal    = document.getElementById('ai-generate-modal');
    const textarea = document.getElementById('ai-gen-input');

    const box = modal?.querySelector('.ai-gen-modal__box');

    // ── helpers to show/hide steps ──
    const showStep = (id) => {
      ['ai-gen-step-input', 'ai-gen-step-preview', 'ai-gen-step-loading', 'ai-gen-step-result']
        .forEach(s => {
          const el = document.getElementById(s);
          if (el) el.hidden = (s !== id);
        });
      // wider box when showing structured steps
      box?.classList.toggle('is-wide', id === 'ai-gen-step-preview');
    };

    const updateSubmitEnabled = () => {
      const submitBtn = document.getElementById('ai-gen-submit');
      if (!submitBtn) return;
      const hasText = (textarea?.value || '').trim().length > 0;
      const hasDoc  = (this._uploadedDocs || []).some(d => d && d.text && d.text.trim().length > 0);
      submitBtn.disabled = !(hasText || hasDoc);
    };

    const resetModal = () => {
      this._panel.stopModalRotation();
      if (textarea) textarea.value = '';
      const submitBtn = document.getElementById('ai-gen-submit');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Estructurar →';
      }
      this._uploadedDocs = [];
      this._currentOutline = '';
      this._currentActors = [];
      this._outlineEditing = false;
      this._inputFormat = 'prose';
      this._lastSuggestions = [];
      this._appliedSuggestionIds = new Set();
      const filesEl = document.getElementById('ai-gen-files');
      if (filesEl) filesEl.innerHTML = '';
      const fileInput = document.getElementById('ai-gen-file');
      if (fileInput) fileInput.value = '';
      const editBtn = document.getElementById('ai-gen-edit');
      if (editBtn) {
        editBtn.textContent = '✏️ Editar';
        delete editBtn.dataset.editing;
      }
      const suggestionsEl = document.getElementById('ai-gen-suggestions');
      if (suggestionsEl) suggestionsEl.hidden = true;
      showStep('ai-gen-step-input');
      this._pendingDesc = null;
      this._generatedXml = null;
      if (modal) modal.hidden = true;
    };

    // ── close / cancel ──
    document.getElementById('ai-gen-modal-close')?.addEventListener('click', resetModal);
    document.getElementById('ai-gen-cancel')?.addEventListener('click', resetModal);
    modal?.querySelector('.ai-gen-modal__backdrop')?.addEventListener('click', resetModal);

    // ── Step 1: textarea input ──
    textarea?.addEventListener('input', updateSubmitEnabled);

    // ── Step 1: PDF drop-zone (§3.1) ──
    this._mountDropzone(updateSubmitEnabled);

    document.getElementById('ai-gen-submit')?.addEventListener('click', async () => {
      const text = (textarea?.value || '').trim();
      const docs = (this._uploadedDocs || []).filter(d => d && d.text && d.text.trim().length > 0);
      if (!text && docs.length === 0) return;

      const language = 'es';
      this._cancelInFlight();

      // Always structure first → show typed-outline preview.
      // The previous "edit mode" path that bypassed describe is removed (was a bug:
      // the user's edit got discarded server-side because no structured_description
      // travelled with the request — see plan §3.3).
      showStep('ai-gen-step-loading');
      this._panel.startModalRotation('structure');

      try {
        const result = await describe(text, language, docs.map(d => ({ name: d.name, text: d.text })));
        this._panel.stopModalRotation();

        // Camino 2: prefer NaLa2BPMN prose if the backend sent it. The user
        // edits prose directly and DSPy consumes it verbatim. Fall back to the
        // legacy typed outline only if prose is missing (older backend builds).
        const prose = (result.structured_prose || '').trim();
        const outline = (result.structured_description || '').trim();
        const detected = result.detected_elements || {};
        const actorsFromBackend = Array.isArray(detected.actors) ? detected.actors : [];

        let preview;
        if (prose) {
          preview = prose;
          this._inputFormat = 'prose';
        } else if (outline) {
          preview = outline;
          this._inputFormat = 'outline';
        } else if (Array.isArray(result.steps) && result.steps.length) {
          preview = result.steps.map(s => `TAREA: ${s}`).join('\n');
          this._inputFormat = 'outline';
        } else {
          preview = result.description || '';
          this._inputFormat = 'prose';
        }

        this._currentOutline = preview;
        this._currentActors  = actorsFromBackend;
        this._renderPreview(preview, result.outline_warnings || []);

        this._pendingDesc = {
          description: result.description || text,
          language,
          structured_description: preview,
        };
        showStep('ai-gen-step-preview');
      } catch (err) {
        this._panel.stopModalRotation();
        resetModal();
        this._panel.open();
        this._panel.addErrorBubble(`No se pudo estructurar: ${err.message}`, null);
      }
    });

    // ── Mic button (step 1) ──
    const micBtn = document.getElementById('ai-gen-mic');
    if (micBtn) {
      if (!this._voice.isSupported()) {
        micBtn.disabled = true;
        micBtn.title = 'Tu navegador no soporta grabación de voz';
      } else {
        const micLabel = micBtn.querySelector('.ai-mic-label');
        micBtn.addEventListener('click', async () => {
          if (this._voice.isRecording()) {
            this._voice.stopRecording();
            micBtn.classList.remove('is-recording');
            micLabel.textContent = 'Grabar voz';
            return;
          }
          micBtn.classList.add('is-recording');
          micLabel.textContent = 'Detener grabación';
          try {
            await this._voice.startRecording(async (blob) => {
              micBtn.classList.remove('is-recording');
              micBtn.classList.add('is-transcribing');
              micLabel.textContent = 'Transcribiendo…';
              micBtn.disabled = true;
              try {
                const text = await transcribe(blob);
                textarea.value = text;
                document.getElementById('ai-gen-submit').disabled = text.trim().length === 0;
              } catch (err) {
                alert(`Error al transcribir: ${err.message}`);
              } finally {
                micBtn.classList.remove('is-transcribing');
                micLabel.textContent = 'Grabar voz';
                micBtn.disabled = false;
              }
            });
          } catch (err) {
            micBtn.classList.remove('is-recording');
            micLabel.textContent = 'Grabar voz';
            alert(`Micrófono no disponible: ${err.message}`);
          }
        });
      }
    }

    // ── Step 2: edit outline inline (§3.3) ──
    // The "Editar" button toggles between read-only coloured render and a monospace
    // textarea. Pressing "✓ Listo" reparses, validates and re-renders coloured.
    // The edit is *authoritative*: whatever text is in the buffer at confirm time
    // travels as `structured_description` to /generate-bpmn.
    document.getElementById('ai-gen-edit')?.addEventListener('click', () => {
      const editBtn = document.getElementById('ai-gen-edit');
      const outlineEl = document.getElementById('ai-gen-outline');
      if (!editBtn || !outlineEl) return;

      if (this._outlineEditing) {
        // Finishing edit → take textarea value as the new content
        const ta = outlineEl.querySelector('#ai-gen-outline-edit');
        const newText = ta ? ta.value : this._currentOutline;
        this._currentOutline = newText;
        if (this._pendingDesc) this._pendingDesc.structured_description = newText;
        this._outlineEditing = false;
        outlineEl.classList.remove('is-editing');
        editBtn.textContent = '✏️ Editar';
        delete editBtn.dataset.editing;

        // Edits invalidate any prior LLM suggestions (the lines they referenced
        // may have moved). Hide the panel until the user clicks "Sugerir mejoras"
        // again.
        this._lastSuggestions = [];
        this._appliedSuggestionIds = new Set();
        const suggestionsPanel = document.getElementById('ai-gen-suggestions');
        if (suggestionsPanel) suggestionsPanel.hidden = true;

        // Repaint with the appropriate renderer for the current input format.
        this._renderPreview(this._currentOutline || '', []);
      } else {
        // Entering edit mode → swap to textarea
        this._outlineEditing = true;
        outlineEl.classList.add('is-editing');
        outlineEl.innerHTML = '';
        const ta = document.createElement('textarea');
        ta.id = 'ai-gen-outline-edit';
        ta.className = 'ai-gen-outline-textarea';
        ta.spellcheck = false;
        ta.value = this._currentOutline || '';
        ta.rows = 14;
        outlineEl.appendChild(ta);
        ta.focus();
        editBtn.textContent = '✓ Listo';
        editBtn.dataset.editing = 'true';
      }
    });

    // ── Step 2: "Sugerir mejoras" — LLM-driven advisory tips. ──
    // Only valid in outline mode for now; the suggest endpoint targets typed
    // outline shape. In prose mode the button is hidden via the renderer's
    // CSS scope (atlas-prose-mode). Wiring stays generic so old outline flow
    // keeps working when prose isn't available from the backend.
    document.getElementById('ai-gen-suggest')?.addEventListener('click', () => {
      if (this._inputFormat !== 'outline') return;  // no-op in prose mode
      // If user is mid-edit, commit the buffer first so we suggest over what
      // they've already typed.
      if (this._outlineEditing) {
        const editBtn = document.getElementById('ai-gen-edit');
        if (editBtn) editBtn.click();
      }
      this._runSuggestOutline();
    });

    document.getElementById('ai-gen-confirm')?.addEventListener('click', async () => {
      // If user is still editing, commit the buffer first.
      if (this._outlineEditing) {
        const editBtn = document.getElementById('ai-gen-edit');
        if (editBtn) editBtn.click();
      }

      if (!this._pendingDesc) return;
      const { description, language, structured_description } = this._pendingDesc;

      showStep('ai-gen-step-loading');
      this._panel.startModalRotation('generate');

      this._cancelInFlight();
      this._abortController = new AbortController();
      this._state = STATE.LOADING;

      try {
        const result = await generateBpmn(
          description,
          language,
          this._abortController.signal,
          structured_description,
          this._inputFormat === 'outline',  // outlineAuthoritative only meaningful in outline mode
          this._inputFormat,                 // 'prose' (Camino 2 — DSPy) | 'outline' (legacy)
        );
        this._generatedXml = result.bpmn_xml;

        // Apply directly to the canvas — no intermediate "result" step. The
        // user already reviewed the prose/outline before clicking Generate, so
        // a confirmation popup adds friction without value.
        if (this._generatedXml && window.epxFlow) {
          try {
            await window.epxFlow.importXML(this._generatedXml, { autoFit: false });
            await window.epxFlow.autoLayout();
            window.epxFlow.fit();
          } catch (err) {
            console.error('[AI] importXML (auto-apply) failed:', err);
            this._panel.stopModalRotation();
            resetModal();
            this._panel.open();
            this._panel.addErrorBubble(`No se pudo aplicar: ${err.message}`, null);
            return;
          }
        }
        resetModal();
      } catch (err) {
        if (err.name === 'AbortError') { resetModal(); return; }
        this._panel.stopModalRotation();
        resetModal();
        this._panel.open();
        this._panel.addErrorBubble(`Error al generar: ${err.message}`, null);
      } finally {
        this._panel.stopModalRotation();
        this._abortController = null;
        this._state = STATE.IDLE;
      }
    });

    // ── Step 4: apply / close ──
    document.getElementById('ai-gen-apply')?.addEventListener('click', async () => {
      if (this._generatedXml && window.epxFlow) {
        try {
          await window.epxFlow.importXML(this._generatedXml, { autoFit: false });
          await window.epxFlow.autoLayout();
          window.epxFlow.fit();
        } catch (err) {
          console.error('[AI] importXML (modal) failed:', err);
          resetModal();
          this._panel.open();
          this._panel.addErrorBubble(`No se pudo aplicar: ${err.message}`, null);
          return;
        }
      }
      resetModal();
    });

    document.getElementById('ai-gen-close-done')?.addEventListener('click', resetModal);
  }

  // ── Generate modal — PDF drop-zone (§3.1) ──────────────────────────────────

  _mountDropzone(updateSubmitEnabled) {
    const dropzone = document.getElementById('ai-gen-dropzone');
    const fileInput = document.getElementById('ai-gen-file');
    if (!dropzone || !fileInput) return;

    const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
    const MAX_FILES = 5;
    const ALLOWED_TYPE = 'application/pdf';

    // Click anywhere on the dropzone (except the inner list buttons) opens the picker.
    dropzone.addEventListener('click', (ev) => {
      if (ev.target.closest('.ai-gen-file__remove')) return;
      if (ev.target === fileInput) return;
      fileInput.click();
    });
    dropzone.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        fileInput.click();
      }
    });

    fileInput.addEventListener('change', (ev) => {
      const list = Array.from(ev.target.files || []);
      this._ingestFiles(list, { MAX_FILE_BYTES, MAX_FILES, ALLOWED_TYPE }, updateSubmitEnabled);
      // Allow re-selecting the same file later
      fileInput.value = '';
    });

    dropzone.addEventListener('dragover', (ev) => {
      ev.preventDefault();
      dropzone.classList.add('is-dragging');
    });
    dropzone.addEventListener('dragleave', () => {
      dropzone.classList.remove('is-dragging');
    });
    dropzone.addEventListener('drop', (ev) => {
      ev.preventDefault();
      dropzone.classList.remove('is-dragging');
      const list = Array.from(ev.dataTransfer?.files || []);
      this._ingestFiles(list, { MAX_FILE_BYTES, MAX_FILES, ALLOWED_TYPE }, updateSubmitEnabled);
    });
  }

  async _ingestFiles(files, limits, updateSubmitEnabled) {
    if (!Array.isArray(files) || files.length === 0) return;
    const filesEl = document.getElementById('ai-gen-files');

    for (const file of files) {
      if (this._uploadedDocs.length >= limits.MAX_FILES) {
        alert(`Solo se admiten hasta ${limits.MAX_FILES} archivos.`);
        break;
      }
      if (file.type && file.type !== limits.ALLOWED_TYPE) {
        alert(`"${file.name}" no es un PDF — se omite.`);
        continue;
      }
      // Some browsers leave file.type empty for drops; fall back to extension check.
      if (!file.type && !/\.pdf$/i.test(file.name || '')) {
        alert(`"${file.name}" no parece un PDF — se omite.`);
        continue;
      }
      if (file.size > limits.MAX_FILE_BYTES) {
        alert(`"${file.name}" supera 10 MB — se omite.`);
        continue;
      }

      // Push a placeholder row so the user sees feedback while extraction runs
      const slot = { name: file.name, text: '', sizeKb: Math.round(file.size / 1024), pages: 0, charCount: 0 };
      this._uploadedDocs.push(slot);
      const rowId = this._renderFileRow(filesEl, slot, { loading: true });

      try {
        const res = await extractDocument(file);
        slot.text = res.text || '';
        slot.pages = res.pages || 0;
        slot.charCount = res.char_count || (slot.text ? slot.text.length : 0);
        this._updateFileRow(rowId, slot, { loading: false, error: null });
      } catch (err) {
        slot.text = '';
        this._updateFileRow(rowId, slot, { loading: false, error: err.message || 'Error' });
      } finally {
        if (typeof updateSubmitEnabled === 'function') updateSubmitEnabled();
      }
    }
  }

  _renderFileRow(container, slot, { loading } = {}) {
    if (!container) return null;
    const li = document.createElement('li');
    const id = `ai-gen-file-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    li.id = id;
    if (loading) li.classList.add('is-loading');
    li.innerHTML = `
      <span class="ai-gen-file__name" title="${slot.name}">📄 ${slot.name}</span>
      <span class="ai-gen-file__meta">${slot.sizeKb} KB${loading ? ' · extrayendo…' : ''}</span>
      <button type="button" class="ai-gen-file__remove" title="Quitar" aria-label="Quitar archivo">✕</button>
    `;
    li.querySelector('.ai-gen-file__remove')?.addEventListener('click', () => {
      const idx = this._uploadedDocs.indexOf(slot);
      if (idx >= 0) this._uploadedDocs.splice(idx, 1);
      li.remove();
      const submitBtn = document.getElementById('ai-gen-submit');
      if (submitBtn) {
        const textareaEl = document.getElementById('ai-gen-input');
        const hasText = (textareaEl?.value || '').trim().length > 0;
        const hasDoc = this._uploadedDocs.some(d => d && d.text && d.text.trim().length > 0);
        submitBtn.disabled = !(hasText || hasDoc);
      }
    });
    container.appendChild(li);
    return id;
  }

  _updateFileRow(id, slot, { loading, error } = {}) {
    if (!id) return;
    const li = document.getElementById(id);
    if (!li) return;
    li.classList.toggle('is-loading', !!loading);
    li.classList.toggle('is-error', !!error);
    const meta = li.querySelector('.ai-gen-file__meta');
    if (meta) {
      if (error) {
        meta.textContent = `${slot.sizeKb} KB · ${error}`;
      } else if (slot.charCount === 0) {
        meta.textContent = `${slot.sizeKb} KB · sin texto extraído (¿PDF escaneado?)`;
      } else {
        meta.textContent = `${slot.sizeKb} KB · ${slot.pages || '?'} pág · ${slot.charCount} chars`;
      }
    }
  }

  // ── Generate modal — outline preview helpers (§3.2) ────────────────────────

  _renderPreview(text, warningsFromBackend = []) {
    const outlineEl = document.getElementById('ai-gen-outline');
    if (!outlineEl) return;
    outlineEl.classList.remove('is-editing');

    // Mark the step container so CSS can hide/show outline-vs-prose-only UI
    // (legend, suggest button, hint text).
    const stepEl = document.getElementById('ai-gen-step-preview');
    if (stepEl) stepEl.dataset.inputFormat = this._inputFormat;
    const hintEl = stepEl?.querySelector('.ai-gen-modal__hint');
    if (hintEl) {
      hintEl.textContent = this._inputFormat === 'prose'
        ? 'Revisá la descripción del proceso. Editá libremente: actores, decisiones e inicio/fin se resaltan. Atlas la usará para generar el BPMN.'
        : 'Revisá el proceso. Cada color indica el tipo de nodo. Podés editar antes de generar.';
    }

    if (this._inputFormat === 'prose') {
      // Camino 2 — prose-with-cues. validateProse only emits info-level signal;
      // never blocks generation. DSPy is the authority on semantics.
      outlineEl.classList.add('atlas-prose-mode');
      outlineEl.classList.remove('atlas-outline-mode');
      renderProseHtml(text, outlineEl, { actors: this._currentActors || [] });
    } else {
      // Legacy typed-outline path.
      outlineEl.classList.add('atlas-outline-mode');
      outlineEl.classList.remove('atlas-prose-mode');
      const localIssues = validateOutline(parseOutline(text || ''));
      const merged = [
        ...warningsFromBackend.map(w => ({
          line: w.line ?? 0,
          code: w.code || 'backend',
          message: w.message || '',
          severity: w.severity || 'warning',
        })),
        ...localIssues,
      ];
      const seen = new Set();
      const dedup = merged.filter(i => {
        const k = `${i.line}|${i.code}|${i.message}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      renderOutlineHtml(text, outlineEl, { issuesByLine: this._buildIssuesByLine(dedup) });
    }

    // A new preview arrived → drop prior LLM suggestions.
    this._lastSuggestions = [];
    this._appliedSuggestionIds = new Set();
    const suggestionsPanel = document.getElementById('ai-gen-suggestions');
    if (suggestionsPanel) suggestionsPanel.hidden = true;
  }

  /**
   * Group issues by 1-based line number so the renderer can mark the
   * corresponding row red/amber. Issues with line=0 (outline-level) are
   * skipped here because they don't map to any single row.
   */
  _buildIssuesByLine(issues) {
    const map = new Map();
    for (const i of issues || []) {
      const line = Number(i?.line) || 0;
      if (!line) continue;
      if (!map.has(line)) map.set(line, []);
      map.get(line).push(i);
    }
    return map;
  }

  /**
   * BPMN_GENERATION_TWO_PHASE_PLAN — "💡 Sugerir mejoras" button.
   *
   * Calls the LLM-driven ``/suggest-outline`` endpoint and renders the
   * resulting tips with a per-row "Aplicar" button. Suggestions never block
   * BPMN generation — they're advisory.
   */
  async _runSuggestOutline() {
    const btn = document.getElementById('ai-gen-suggest');
    if (btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
    }
    const language = (this._pendingDesc && this._pendingDesc.language) || 'es';
    const outline = this._currentOutline || '';

    let suggestions = [];
    let success = true;
    try {
      const res = await suggestOutline(outline, language, 6);
      suggestions = Array.isArray(res?.suggestions) ? res.suggestions : [];
      success = res?.success !== false;
    } catch (err) {
      console.warn('[Atlas] suggest-outline failed:', err);
      success = false;
    } finally {
      if (btn) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    }

    // Stable IDs so an "applied" mark survives a re-render.
    suggestions.forEach((s, i) => { s._id = `sg-${i}-${s.kind}-${s.line}`; });
    this._lastSuggestions = suggestions;
    this._renderSuggestions(suggestions, { success });
  }

  /**
   * Render the LLM suggestions panel. Each suggestion gets a clickable
   * "Aplicar" button (when there's a line replacement available) that mutates
   * the outline buffer in place and re-runs lint.
   */
  _renderSuggestions(suggestions, { success = true } = {}) {
    const panel = document.getElementById('ai-gen-suggestions');
    const listEl = document.getElementById('ai-gen-suggestions-list');
    const summary = document.getElementById('ai-gen-suggestions-summary');
    if (!panel || !listEl) return;

    panel.hidden = false;
    listEl.innerHTML = '';

    if (!suggestions || suggestions.length === 0) {
      summary?.classList.add('ai-gen-suggestions--empty');
      if (summary) {
        summary.textContent = success
          ? '✓ Sin sugerencias adicionales — el outline se ve bien.'
          : '⚠ No se pudieron obtener sugerencias (¿LLM no configurado?).';
      }
      return;
    }
    summary?.classList.remove('ai-gen-suggestions--empty');
    if (summary) {
      summary.textContent = `💡 ${suggestions.length} sugerencia${suggestions.length === 1 ? '' : 's'} de mejora`;
    }

    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    suggestions.forEach((s) => {
      const li = document.createElement('li');
      const isApplied = this._appliedSuggestionIds?.has(s._id);
      if (isApplied) li.classList.add('is-applied');
      const lineLabel = s.line ? `L${s.line}` : '—';
      const canApply = (typeof s.after === 'string' && s.after.trim().length > 0);
      const diff = canApply
        ? `<span class="ai-gen-suggestions__diff">
             ${s.before ? `<span class="diff-before">${esc(s.before)}</span><br/>` : ''}
             <span class="diff-after">${esc(s.after)}</span>
           </span>`
        : '';
      const rationale = s.rationale
        ? `<span class="ai-gen-suggestions__rationale">${esc(s.rationale)}</span>`
        : '';
      li.innerHTML = `
        <span class="ai-gen-suggestions__icon" aria-hidden="true">💡</span>
        <span class="ai-gen-suggestions__body">
          <span class="ai-gen-suggestions__line">${lineLabel}</span>
          <span class="ai-gen-suggestions__kind">${esc(s.kind || 'other')}</span>
          ${esc(s.message)}
          ${diff}
          ${rationale}
        </span>
        ${canApply
          ? `<button type="button" class="ai-gen-suggestions__apply"${isApplied ? ' disabled' : ''}>${isApplied ? '✓ Aplicado' : 'Aplicar'}</button>`
          : ''}
      `;
      const btn = li.querySelector('.ai-gen-suggestions__apply');
      if (btn) {
        btn.addEventListener('click', () => this._applyOutlineSuggestion(s, li));
      }
      // Clicking the body scrolls to the relevant outline line.
      const body = li.querySelector('.ai-gen-suggestions__body');
      if (body && s.line) {
        body.style.cursor = 'pointer';
        body.addEventListener('click', () => this._focusOutlineLine(s.line));
      }
      listEl.appendChild(li);
    });
  }

  /**
   * Apply a single suggestion to the outline buffer.
   *
   *   - If ``before`` matches a line, replace that line with ``after``.
   *   - Else if only ``after`` is present, insert it after ``line`` (or at the
   *     end when line=0).
   *
   * After mutation we re-render the preview, re-run lint, and mark the row
   * as applied so the user sees instant feedback.
   */
  _applyOutlineSuggestion(suggestion, rowEl) {
    if (!suggestion || typeof suggestion.after !== 'string' || !this._currentOutline) return;

    const lines = this._currentOutline.split('\n');
    let mutated = false;
    if (suggestion.before && typeof suggestion.before === 'string') {
      // Prefer matching by exact text first, then by line number as fallback.
      const idx = lines.indexOf(suggestion.before);
      if (idx >= 0) {
        lines[idx] = suggestion.after;
        mutated = true;
      } else if (suggestion.line && lines[suggestion.line - 1] !== undefined) {
        lines[suggestion.line - 1] = suggestion.after;
        mutated = true;
      }
    } else {
      // Insert after the target line (or at the end).
      const at = (suggestion.line && suggestion.line > 0)
        ? Math.min(suggestion.line, lines.length)
        : lines.length;
      lines.splice(at, 0, suggestion.after);
      mutated = true;
    }
    if (!mutated) return;

    const newText = lines.join('\n');
    this._currentOutline = newText;
    if (this._pendingDesc) this._pendingDesc.structured_description = newText;

    this._appliedSuggestionIds = this._appliedSuggestionIds || new Set();
    this._appliedSuggestionIds.add(suggestion._id);

    if (rowEl) {
      rowEl.classList.add('is-applied');
      const btn = rowEl.querySelector('.ai-gen-suggestions__apply');
      if (btn) {
        btn.textContent = '✓ Aplicado';
        btn.disabled = true;
      }
    }

    // Repaint the outline immediately so the user sees the change.
    const outlineEl = document.getElementById('ai-gen-outline');
    if (outlineEl && !this._outlineEditing) {
      const localIssues = validateOutline(parseOutline(newText));
      renderOutlineHtml(newText, outlineEl, { issuesByLine: this._buildIssuesByLine(localIssues) });
    }
  }

  /**
   * Scroll to the outline row for ``lineNumber`` and flash it briefly. Falls
   * back gracefully if no row matches (e.g. outline was edited after lint).
   */
  _focusOutlineLine(lineNumber) {
    const outlineEl = document.getElementById('ai-gen-outline');
    if (!outlineEl) return;
    const row = outlineEl.querySelector(`.atlas-out[data-line="${lineNumber}"]`);
    if (!row) return;
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    row.classList.remove('is-flash');
    // Force reflow so the animation re-triggers if the user clicks twice.
    void row.offsetWidth;
    row.classList.add('is-flash');
    window.setTimeout(() => row.classList.remove('is-flash'), 1300);
  }

  // ── Chip click ──────────────────────────────────────────────────────────────

  _onChip(chipLabel) {
    const intent = getIntentForChip(chipLabel);

    if (intent === 'generate') {
      if (this._mode === 'bpmn') {
        const modal = document.getElementById('ai-generate-modal');
        if (modal) {
          modal.hidden = false;
          document.getElementById('ai-gen-input')?.focus();
        }
        return;
      }
      // PR 4 §4.3 — Rules mode: open the DMN wizard. Lazy-mount so a session
      // that never enters DMN never creates the modal listeners.
      this._openDmnGenerateModal();
      return;
    }

    const input = document.getElementById('ai-input');

    // Suggest-options chips: auto-send with a context-aware message — no user typing needed

    // 🔗 Conectar nodos: native, no AI — connects selected nodes left-to-right by position
    if (chipLabel === '🔗 Conectar nodos') {
      this._connectSelectedNodes();
      return;
    }

    // PR 3 §4.4 — Native DMN handlers (no network, mutate model via epxDmn).
    // Routed by intent so the chip emoji / locale is free to vary.
    if (this._mode === 'rules' && isNativeIntent(intent)) {
      this._dispatchNativeDmnIntent(intent, chipLabel);
      return;
    }

    if (intent === 'suggest_options') {
      const SUGGEST_PREFILLS = {
        '✏️ Renombrar':             'Sugiere nombres alternativos para este elemento',
        '➕ Agregar siguiente paso': 'Sugiere qué paso agregar después de este elemento',
        '🔄 Cambiar tipo':          'Sugiere a qué tipo cambiar este elemento',
        '🔀 Cambiar tipo':          'Sugiere a qué tipo cambiar este gateway',
      };
      this._lastChip = chipLabel;
      input.value = SUGGEST_PREFILLS[chipLabel] ?? chipLabel;
      document.getElementById('ai-send').disabled = false;
      this._send('suggest_options');
      return;
    }

    // Modify chips: prefill and focus so user specifies exactly what to change.
    // Don't auto-send — vague text like "Renombrar" produces LLM responses without XML.
    if (intent === 'modify') {
      const PREFILLS = {
        '⚙️ Configurar condiciones': 'Configurar las condiciones del gateway: ',
        '✏️ Agregar condición':     'Agregar condición al flujo: ',
        '✏️ Modificar grupo':       'Modificar los elementos seleccionados: ',
        '✏️ Modificar regla':       'Modificar la regla: ',
      };
      input.value = PREFILLS[chipLabel] ?? (chipLabel.replace(/^[^\w]+/, '') + ': ');
      document.getElementById('ai-send').disabled = false;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
      return;
    }

    input.value = chipLabel;
    document.getElementById('ai-send').disabled = false;
    this._send(intent);
  }

  // ── Send (free text / chip text) ───────────────────────────────────────────

  // apiMessage: optional override sent to the API — allows showing a short display text
  // in the chat while sending a richer technical message to the model.
  async _send(forcedIntent = null, apiMessage = null) {
    const input = document.getElementById('ai-input');
    const text = input.value.trim();
    if (!text) return;
    if (this._state === STATE.LOADING) return;

    input.value = '';
    document.getElementById('ai-send').disabled = true;

    const intent = forcedIntent || detectIntent(text);

    // BPMN generate → modal wizard; DMN generate → normal chat flow with domain:dmn
    if (intent === 'generate' && this._mode === 'bpmn') {
      const modal = document.getElementById('ai-generate-modal');
      if (modal) {
        document.getElementById('ai-gen-input').value = text;
        document.getElementById('ai-gen-submit').disabled = false;
        modal.hidden = false;
      }
      return;
    }

    this._cancelInFlight();
    this._abortController = new AbortController();
    this._state = STATE.LOADING;

    // Capture mode at request start to avoid race if user switches panels
    const mode         = this._mode;
    const decisionRef  = this._decisionRef;
    const domain       = mode === 'rules' ? 'dmn' : 'bpmn';
    const loadingKey   = mode === 'rules' ? `dmn_${intent}` : intent;

    // text is shown in the chat; apiMessage (if provided) is what actually goes to the model
    const messageForApi = apiMessage || text;

    const history = this._panel.loadHistory();
    if (!history.length) this._panel.renameActiveTab(text);
    this._panel.addUserMessage(text);
    const loadingRef = this._panel.addLoadingBubble(loadingKey);

    try {
      let context;
      if (mode === 'rules') {
        // WT-G — thread intent into the builder so it knows whether to attach
        // validator_issues[] (only for dmn_validate_*) and which expression
        // path to populate.
        context = await buildDmnContext(decisionRef, { intent });
      } else {
        const includeSelection = document.getElementById('ai-include-selection')?.checked ?? true;
        context = await buildContext(includeSelection, includeSelection ? this._selection : []);
      }

      const response = await assist(
        {
          domain,
          intent,
          message: messageForApi,
          context,
          history: history.slice(-20),
        },
        this._abortController.signal
      );

      // Validate — show structured issues list. DMN coverage validation also
      // returns issues[] but under a different intent; render the same bubble
      // so the user sees what failed (instead of just an "Aplicar" button).
      const isIssuesIntent = intent === 'validate' || intent === 'dmn_validate_coverage';
      if (isIssuesIntent && response.issues?.length > 0) {
        this._panel.replaceLoading(loadingRef, response.message || null);
        this._panel.addIssuesBubble(response.issues, (elementId) => {
          if (mode === 'bpmn') this._highlightElement(elementId);
          else if (mode === 'rules') this._dmnApplyHighlightDecision(window.epxDmn, { decisionId: elementId });
        });
        // Coverage validation can also include highlight_rules / suggest_rule
        // actions — surface them as a second bubble so the user can apply.
        if (mode === 'rules') {
          const followups = this._buildActions(response, mode);
          if (followups.length) this._panel.addAiMessage('', followups);
        }
      } else if (intent === 'suggest_options' && response.suggestions?.length > 0) {
        // Suggestion mode — render option pills; apply natively without re-importing XML
        const lastChip = this._lastChip;
        this._panel.replaceLoading(loadingRef, null);
        this._panel.addSuggestionsBubble(
          response.message,
          response.suggestions,
          (value) => this._applySuggestion(lastChip, value)
        );
      } else if (intent === 'modify' && !response.modified_xml) {
        // The LLM responded but without modified_xml — log full response for debugging
        console.warn('[AI] modify intent returned no modified_xml. Full response:', response);
        this._panel.replaceLoading(loadingRef, null);
        this._panel.addErrorBubble(
          'No se pudo generar el XML del cambio. Intenta de nuevo.',
          () => {
            document.getElementById('ai-input').value = text;
            document.getElementById('ai-send').disabled = false;
            this._send(forcedIntent, apiMessage);
          }
        );
      } else {
        const actions = this._buildActions(response, mode);
        this._panel.replaceLoading(loadingRef, response.message, actions);
      }

      this._panel.saveHistory([
        ...history,
        { role: 'user',      content: text },
        { role: 'assistant', content: response.message },
      ]);

    } catch (err) {
      if (err.name === 'AbortError') {
        this._panel.replaceLoading(loadingRef, null);
        return;
      }
      this._panel.replaceLoading(loadingRef, null);
      this._panel.addErrorBubble(err.message, () => {
        document.getElementById('ai-input').value = text;
        document.getElementById('ai-send').disabled = false;
        this._send(forcedIntent);
      });
    } finally {
      document.getElementById('ai-send').disabled = false;
      this._abortController = null;
      this._state = STATE.IDLE;
    }
  }

  // ── Generate — step 1: describe ───────────────────────────────────────────

  async _startGenerate(text) {
    if (this._state === STATE.LOADING) return;
    this._state = STATE.LOADING;

    this._panel.addUserMessage(text);
    const loadingRef = this._panel.addLoadingBubble('generate');

    const language = (navigator.language || 'en').startsWith('es') ? 'es' : 'en';

    try {
      const result = await describe(text, language);
      this._panel.replaceLoading(loadingRef, null);

      const steps = result.steps?.length ? result.steps : [result.description];

      this._state = STATE.WAITING_CONFIRM;
      this._pendingDesc = { description: result.description, language, structured_description: result.structured_description ?? null };

      this._panel.addStepsBubble(
        steps,
        result.description,
        (desc) => this._confirmGenerate(desc, language, result.structured_description ?? null),
        (desc) => this._editGenerate(desc)
      );

    } catch (err) {
      this._panel.replaceLoading(loadingRef, null);
      this._panel.addErrorBubble(err.message, () => this._startGenerate(text));
      this._state = STATE.IDLE;
    }
  }

  // ── Generate — step 2: generate-bpmn ─────────────────────────────────────

  async _confirmGenerate(description, language, structured_description = null) {
    this._state = STATE.LOADING;
    this._pendingDesc = null;

    this._cancelInFlight();
    this._abortController = new AbortController();

    const loadingRef = this._panel.addLoadingBubble('generate');

    try {
      const result = await generateBpmn(description, language, this._abortController.signal, structured_description);

      const actions = [];
      if (result.bpmn_xml) {
        actions.push({
          label:   '✓ Aplicar al canvas',
          primary: true,
          onClick: async () => {
            try {
              await window.epxFlow.importXML(result.bpmn_xml, { autoFit: false });
              await window.epxFlow.autoLayout();
              window.epxFlow.fit();
            } catch (err) {
              console.error('[AI] importXML failed:', err);
              this._panel.addErrorBubble(`No se pudo aplicar: ${err.message}`, null);
            }
          },
        });
        actions.push({
          label:   '✕ Cancelar',
          primary: false,
          onClick: () => {},
        });
      }

      this._panel.replaceLoading(
        loadingRef,
        result.message || 'BPMN generado. Revisalo y aplicalo al canvas.',
        actions
      );

    } catch (err) {
      if (err.name === 'AbortError') {
        this._panel.replaceLoading(loadingRef, null);
        return;
      }
      this._panel.replaceLoading(loadingRef, null);
      this._panel.addErrorBubble(err.message, () => this._confirmGenerate(description, language, structured_description));
    } finally {
      this._abortController = null;
      this._state = STATE.IDLE;
    }
  }

  // ── Generate — edit: put description back in input ────────────────────────

  _editGenerate(description) {
    this._state = STATE.IDLE;
    this._pendingDesc = null;
    const input = document.getElementById('ai-input');
    input.value = description;
    document.getElementById('ai-send').disabled = false;
    input.focus();
  }

  // ── Highlight element in canvas ───────────────────────────────────────────

  _highlightElement(elementId) {
    const flow = window.epxFlow;
    if (!flow) return;
    try {
      const lf = flow.getLogicFlow();
      // Deselect all then select the target
      lf.clearSelectElements();
      lf.selectElementById(elementId);
      // Scroll the element into view
      const model = lf.graphModel.getElement(elementId);
      if (model) {
        lf.focusOn({ id: elementId });
      }
    } catch (e) {
      console.warn('[AI] highlight failed for', elementId, e);
    }
  }

  // ── Build inline action buttons from response ────────────────────────────

  _buildActions(response, mode = 'bpmn') {
    const actions = [];
    const isDmn = mode === 'rules';

    // WT-G — DMN structured actions[] (THEMIS_DMN_PLAN §4.4) take precedence
    // over modified_xml when both are present. We render a single composite
    // "Aplicar cambios" button that walks the list and invokes the matching
    // epxDmn helper for each. Unknown action types are ignored with a warning.
    const dmnActionEntries = isDmn
      ? (response.actions || []).filter((a) => a && a.type && this._isDmnStructuredAction(a.type))
      : [];

    if (dmnActionEntries.length > 0) {
      const summary = dmnActionEntries.length === 1
        ? `✓ Aplicar (${dmnActionEntries[0].type})`
        : `✓ Aplicar ${dmnActionEntries.length} cambios`;
      actions.push({
        label:   summary,
        primary: true,
        onClick: async () => {
          const applied = this._applyDmnStructuredActions(dmnActionEntries);
          if (applied === 0) {
            this._panel.addErrorBubble('No se pudo aplicar ninguna acción.', null);
          }
        },
      });
      actions.push({
        label:   '✕ Cancelar',
        primary: false,
        onClick: () => {},
      });
      return actions;
    }

    if (response.modified_xml) {
      actions.push({
        label:   isDmn ? '✓ Aplicar al editor' : '✓ Aplicar al canvas',
        primary: true,
        onClick: async () => {
          try {
            if (isDmn) {
              window.epxDmn.importXML(response.modified_xml);
            } else {
              await window.epxFlow.importXML(response.modified_xml, { autoFit: false });
              await window.epxFlow.autoLayout();
            }
          } catch (err) {
            console.error('[AI] importXML failed:', err);
            this._panel.addErrorBubble(`No se pudo aplicar: ${err.message}`, null);
          }
        },
      });
      actions.push({
        label:   '✕ Cancelar',
        primary: false,
        onClick: () => {},
      });
    }

    // Copy button for document/explain responses
    if (!response.modified_xml && response.message?.length > 200) {
      actions.push({
        label:   '📋 Copiar',
        primary: false,
        onClick: () => navigator.clipboard?.writeText(response.message),
      });
    }

    return actions;
  }

  // ── WT-G — Catalogue of structured DMN actions[] we know how to apply ────

  _isDmnStructuredAction(type) {
    return DMN_STRUCTURED_ACTIONS.has(type);
  }

  /**
   * Applies a list of structured actions returned by the LLM
   * (THEMIS_DMN_PLAN §4.4 + THEMIS_ML_DECISION_PLAN §5.4). Each entry has
   * `{type, params, payload?}`. Unknown types log a warning and are skipped.
   * Returns the number of actions actually applied.
   */
  _applyDmnStructuredActions(list) {
    const dmn = window.epxDmn;
    if (!dmn) return 0;
    let applied = 0;
    for (const a of list) {
      const params = (a && (a.params || a.payload)) || {};
      try {
        switch (a.type) {
          case 'rename': {
            const ok = this._dmnApplyRename(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'set_description': {
            const ok = this._dmnApplyDescription(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'set_type': {
            const ok = this._dmnApplyTypeRef(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'add_rule': {
            const ok = this._dmnApplyAddRule(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'add_input_column':
          case 'add_output_column': {
            const ok = this._dmnApplyAddColumn(dmn, a.type, params);
            if (ok) applied++;
            break;
          }
          case 'set_hit_policy': {
            const ok = this._dmnApplyHitPolicy(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'set_feel': {
            const ok = this._dmnApplyFeel(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'add_context_entry': {
            const ok = this._dmnApplyAddContextEntry(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'add_list_item': {
            const ok = this._dmnApplyAddListItem(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'add_relation_row': {
            const ok = this._dmnApplyAddRelationRow(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'add_relation_column': {
            const ok = this._dmnApplyAddRelationColumn(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'set_function_params': {
            const ok = this._dmnApplyFnParams(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'link_document': {
            const ok = this._dmnApplyLinkDocument(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'add_input_data': {
            const n = this._dmnApplyAddInputData(dmn, params);
            if (n > 0) applied += n;
            break;
          }
          // ── ML decision actions (THEMIS_ML §5.4) ─────────────────────
          case 'set_ml_model':
          case 'add_ml_input_binding':
          case 'remove_ml_input_binding':
          case 'add_ml_output_binding':
          case 'remove_ml_output_binding':
          case 'set_ml_fallback':
          case 'set_ml_threshold': {
            const ok = this._dmnApplyMlAction(dmn, a.type, params);
            if (ok) applied++;
            break;
          }
          // ── Themis simulator + navigation (THEMIS_CHAT_PLAN §3.5) ────
          case 'load_scenarios': {
            const ok = this._dmnApplyLoadScenarios(dmn, a.scenarios || params.scenarios);
            if (ok) applied++;
            break;
          }
          case 'run_scenarios': {
            const ok = this._dmnApplyRunScenarios(dmn);
            if (ok) applied++;
            break;
          }
          case 'open_decision': {
            const ok = this._dmnApplyOpenDecision(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'highlight_decision': {
            const ok = this._dmnApplyHighlightDecision(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'highlight_rules': {
            const ok = this._dmnApplyHighlightRules(dmn, params);
            if (ok) applied++;
            break;
          }
          case 'suggest_rule': {
            // Reuse add_rule under the hood — the LLM uses suggest_rule when
            // the rule is a recommendation (still applied on Apply click,
            // just labelled differently in the prompt).
            const ok = this._dmnApplyAddRule(dmn, params);
            if (ok) applied++;
            break;
          }
          default:
            console.warn('[AI] Unknown DMN action type — ignored:', a.type);
        }
      } catch (err) {
        console.warn('[AI] DMN action threw:', a.type, err);
      }
    }
    return applied;
  }

  // ── action helpers — best-effort wrappers around epxDmn / moddle ────────

  _dmnApplyRename(dmn, { id, name }) {
    if (!id || typeof name !== 'string') return false;
    const node = this._dmnFindNode(dmn, id);
    if (!node) return false;
    if (typeof dmn.rename === 'function') return !!dmn.rename(id, name);
    node.name = name;
    if (node.text) node.text = (typeof node.text === 'object') ? { ...node.text, text: name } : name;
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyDescription(dmn, { id, description }) {
    if (!id) return false;
    const node = this._dmnFindNode(dmn, id);
    if (!node) return false;
    node.description = description == null ? '' : String(description);
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyTypeRef(dmn, { id, typeRef }) {
    if (!id || !typeRef) return false;
    const node = this._dmnFindNode(dmn, id);
    if (!node) return false;
    // InputData / Decision both expose typeRef on their `variable` child.
    if (node.variable) node.variable.typeRef = typeRef;
    else node.typeRef = typeRef;
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyAddRule(dmn, { decisionId, rule } = {}) {
    if (typeof dmn.addRule !== 'function') return false;
    // The LLM occasionally omits decisionId for `dmn_fill_coverage` since the
    // user is already inside the table editor. Fall back to whatever the
    // active table reports so each rule lands in the right decision instead
    // of a stale one.
    const targetId = decisionId || this._activeDecisionId() || null;
    const ref = dmn.addRule(targetId);
    if (!ref) return false;
    // Best-effort: populate the rule's entries when the LLM provided them.
    let mutated = false;
    if (rule) {
      try {
        const dec = this._dmnFindNode(dmn, ref.decisionId || targetId || '');
        const dt  = dec?.decisionLogic;
        const last = dt?.rule?.[dt.rule.length - 1];
        if (last) {
          (rule.input_entries || []).forEach((feel, i) => {
            const ie = last.inputEntry?.[i];
            if (ie && typeof feel === 'string') {
              ie.text = feel;
              mutated = true;
            }
          });
          (rule.output_entries || []).forEach((feel, i) => {
            const oe = last.outputEntry?.[i];
            if (oe && typeof feel === 'string') {
              oe.text = feel;
              mutated = true;
            }
          });
          if (rule.annotation != null) {
            last.description = String(rule.annotation);
            mutated = true;
          }
        }
      } catch (_) { /* keep best-effort */ }
    }
    // The editor re-renders inside `addRule`, BEFORE we set the FEEL text on
    // the new entries — without a second redraw the user sees the default
    // placeholders ("-" / "") instead of the suggested values. Signal a
    // structural change so the table redraws with the populated cells.
    if (mutated) this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyAddColumn(dmn, type, { decisionId, column } = {}) {
    const dec = this._dmnFindNode(dmn, decisionId || this._activeDecisionId() || '');
    const dt = dec?.decisionLogic;
    if (!dt || !column) return false;
    if (type === 'add_input_column') {
      dt.input = dt.input || [];
      dt.input.push({
        $type: 'dmn:InputClause',
        label: column.label || 'input',
        inputExpression: { $type: 'dmn:LiteralExpression', text: column.expression || '', typeRef: column.typeRef || 'string' },
      });
    } else {
      dt.output = dt.output || [];
      dt.output.push({
        $type: 'dmn:OutputClause',
        label: column.label || 'output',
        name:  column.label || 'output',
        typeRef: column.typeRef || 'string',
      });
    }
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyHitPolicy(dmn, { decisionId, hitPolicy } = {}) {
    const dec = this._dmnFindNode(dmn, decisionId || this._activeDecisionId() || '');
    const dt = dec?.decisionLogic;
    if (!dt || !hitPolicy) return false;
    dt.hitPolicy = hitPolicy;
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyFeel(dmn, { exprPath, feel } = {}) {
    if (!feel) return false;
    const target = this._dmnResolveExpression(dmn, exprPath);
    if (!target) return false;
    if ('text' in target) target.text = feel;
    else if (target.literalExpression) target.literalExpression.text = feel;
    else return false;
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyAddContextEntry(dmn, { exprPath, name, value } = {}) {
    const expr = this._dmnResolveExpression(dmn, exprPath) || this._activeExpression();
    if (!expr || expr.$type !== 'dmn:Context') return false;
    const entry = typeof dmn.appendContextEntry === 'function'
      ? dmn.appendContextEntry(expr) : null;
    if (!entry) return false;
    if (name && entry.variable) entry.variable.name = name;
    if (value && entry.value) entry.value.text = value;
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyAddListItem(dmn, { exprPath, value } = {}) {
    const expr = this._dmnResolveExpression(dmn, exprPath) || this._activeExpression();
    if (!expr || expr.$type !== 'dmn:List') return false;
    const item = typeof dmn.appendListItem === 'function' ? dmn.appendListItem(expr) : null;
    if (!item) return false;
    if (value != null) item.text = String(value);
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyAddRelationRow(dmn, { exprPath, row } = {}) {
    const expr = this._dmnResolveExpression(dmn, exprPath) || this._activeExpression();
    if (!expr || expr.$type !== 'dmn:Relation') return false;
    const created = typeof dmn.appendRelationRow === 'function' ? dmn.appendRelationRow(expr) : null;
    if (!created) return false;
    if (Array.isArray(row)) {
      (created.elements || []).forEach((cell, i) => {
        if (row[i] != null && cell) cell.text = String(row[i]);
      });
    }
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyAddRelationColumn(dmn, { exprPath, label, typeRef } = {}) {
    const expr = this._dmnResolveExpression(dmn, exprPath) || this._activeExpression();
    if (!expr || expr.$type !== 'dmn:Relation') return false;
    const col = typeof dmn.appendRelationColumn === 'function' ? dmn.appendRelationColumn(expr) : null;
    if (!col) return false;
    if (label) col.name = label;
    if (typeRef) col.typeRef = typeRef;
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyFnParams(dmn, { id, params } = {}) {
    if (!id || !Array.isArray(params)) return false;
    const node = this._dmnFindNode(dmn, id);
    const fn = node?.decisionLogic || node?.encapsulatedLogic || (node?.$type?.endsWith('FunctionDefinition') ? node : null);
    if (!fn) return false;
    fn.formalParameter = params.map((p) => ({
      $type: 'dmn:InformationItem',
      name:  p.name || '',
      typeRef: p.typeRef || 'string',
    }));
    this._dmnSignalChange(dmn);
    return true;
  }

  // Themis "Sugerir inputs faltantes" — accept the suggestion creates real
  // InputData nodes wired to the focused decision (DRD shape + edge included).
  // Accepts either `inputs: [{name, typeRef}]` or a single `{name, typeRef}`.
  // `decisionId` falls back to the active decision when omitted.
  _dmnApplyAddInputData(dmn, { decisionId, decision_id, inputs, name, typeRef } = {}) {
    const decId = decisionId || decision_id || this._activeDecisionId();
    if (!decId) return 0;
    const list = Array.isArray(inputs)
      ? inputs
      : (name ? [{ name, typeRef: typeRef || 'string' }] : []);
    if (!list.length) return 0;
    if (typeof dmn.addInputDataForDecision !== 'function') return 0;
    return dmn.addInputDataForDecision(decId, list) || 0;
  }

  _dmnApplyLinkDocument(dmn, { id, documentRef } = {}) {
    if (!id || !documentRef) return false;
    const node = this._dmnFindNode(dmn, id);
    if (!node) return false;
    // Knowledge sources expose `locationURI` natively; fall back to a generic
    // description suffix for node kinds that don't. The chat shows what was set.
    if ('locationURI' in node || node.$type?.endsWith(':KnowledgeSource')) {
      node.locationURI = documentRef;
    } else {
      node.description = (node.description ? node.description + '\n' : '') + `[doc] ${documentRef}`;
    }
    this._dmnSignalChange(dmn);
    return true;
  }

  _dmnApplyMlAction(dmn, type, params = {}) {
    const id = params.id || this._activeDecisionId();
    if (!id) return false;
    const node = this._dmnFindNode(dmn, id);
    const ml = node?.decisionLogic;
    if (!ml || ml.$type !== 'epx:MachineLearningDecision') return false;
    switch (type) {
      case 'set_ml_model': {
        if (params.modelRef != null)     ml.modelRef = params.modelRef;
        if (params.modelVersion != null) ml.modelVersion = params.modelVersion;
        if (params.runtime != null)      ml.runtime = params.runtime;
        if (params.endpoint != null)     ml.endpoint = params.endpoint;
        break;
      }
      case 'add_ml_input_binding': {
        ml.inputBinding = ml.inputBinding || [];
        ml.inputBinding.push({
          $type: 'epx:InputBinding',
          name:   params.binding?.name   || params.name   || '',
          source: params.binding?.source || params.source || '',
        });
        break;
      }
      case 'remove_ml_input_binding': {
        const name = params.name;
        if (!name || !ml.inputBinding) return false;
        ml.inputBinding = ml.inputBinding.filter((b) => b.name !== name);
        break;
      }
      case 'add_ml_output_binding': {
        ml.outputBinding = ml.outputBinding || [];
        ml.outputBinding.push({
          $type: 'epx:OutputBinding',
          name:   params.binding?.name   || params.name   || '',
          target: params.binding?.target || params.target || '',
        });
        break;
      }
      case 'remove_ml_output_binding': {
        const name = params.name;
        if (!name || !ml.outputBinding) return false;
        ml.outputBinding = ml.outputBinding.filter((b) => b.name !== name);
        break;
      }
      case 'set_ml_fallback': {
        ml.fallback = {
          $type: 'epx:Fallback',
          strategy: params.strategy || 'fixed_value',
          value:    params.value == null ? undefined : String(params.value),
        };
        break;
      }
      case 'set_ml_threshold': {
        const t = parseFloat(params.confidenceThreshold);
        if (!Number.isFinite(t)) return false;
        ml.confidenceThreshold = t;
        break;
      }
      default:
        return false;
    }
    this._dmnSignalChange(dmn);
    return true;
  }

  // ── Themis simulator + navigation actions (THEMIS_CHAT_PLAN §3.5) ───────

  _dmnApplyLoadScenarios(dmn, scenarios) {
    if (!Array.isArray(scenarios) || !scenarios.length) {
      console.warn('[AI] load_scenarios action arrived without scenarios[] — nothing to load.');
      return false;
    }
    if (!dmn) return false;
    // Force the simulator dock open BEFORE we ask for the panel — `dmn.simulator`
    // is a getter that lazily reads `_simulatorDock._panel`, which only exists
    // after `openSimulator()` mounts it. Calling open is idempotent.
    try { if (typeof dmn.openSimulator === 'function') dmn.openSimulator(); } catch (_) {}
    const sim = dmn.simulator;
    if (!sim || typeof sim.loadScenarios !== 'function') return false;
    // Normalise scenario shape so the SimulationPanel sees the keys it expects.
    // The LLM sometimes uses `expected_outputs` (legacy field name) instead of
    // `expected` — remap so both work and outputs aren't silently dropped.
    const normalised = scenarios
      .filter((s) => s && typeof s === 'object')
      .map((s) => ({
        id:       s.id,
        name:     String(s.name || 'Escenario'),
        inputs:   (s.inputs && typeof s.inputs === 'object') ? s.inputs : {},
        expected: (s.expected && typeof s.expected === 'object')
          ? s.expected
          : (s.expected_outputs && typeof s.expected_outputs === 'object'
              ? s.expected_outputs
              : {}),
      }));
    return sim.loadScenarios(normalised) !== false;
  }

  _dmnApplyRunScenarios(dmn) {
    const sim = dmn && dmn.simulator;
    if (!sim || typeof sim.runAll !== 'function') return false;
    try { if (typeof dmn.openSimulator === 'function' && !sim.isOpen?.()) dmn.openSimulator(); } catch (_) {}
    const results = sim.runAll();
    if (!results) return false;
    // Echo a short summary into the chat so the user sees the run result.
    try {
      const arr = Array.isArray(results) ? results : [];
      const passed = arr.filter((r) => r && (r.ok === true || r.passed === true)).length;
      const failed = arr.length - passed;
      const tone = failed > 0 ? '⚠️' : '✓';
      this._panel.addAiMessage(
        `${tone} Simulación: ${arr.length} escenario(s), ${passed} pasaron, ${failed} fallaron.`,
      );
    } catch (_) { /* non-fatal */ }
    return true;
  }

  _dmnApplyOpenDecision(dmn, { decision_id, decisionId } = {}) {
    const id = decision_id || decisionId;
    if (!id || !dmn || typeof dmn.openDecision !== 'function') return false;
    dmn.openDecision(id);
    return true;
  }

  _dmnApplyHighlightDecision(dmn, { decision_id, decisionId } = {}) {
    const id = decision_id || decisionId;
    if (!id || !dmn) return false;
    // Best-effort: highlight via the DRD's setNodeSimStatus channel so the
    // decision pulses without changing selection. Fallback: open the decision.
    try {
      if (typeof dmn.setNodeSimStatus === 'function') {
        dmn.setNodeSimStatus(id, 'attention');
        setTimeout(() => {
          try { dmn.setNodeSimStatus(id, null); } catch (_) {}
        }, 2500);
        return true;
      }
    } catch (_) { /* fall through */ }
    if (typeof dmn.openDecision === 'function') { dmn.openDecision(id); return true; }
    return false;
  }

  _dmnApplyHighlightRules(dmn, { decision_id, decisionId, rule_indices, ruleIndices } = {}) {
    const id  = decision_id || decisionId;
    const idx = Array.isArray(rule_indices) ? rule_indices
              : Array.isArray(ruleIndices)  ? ruleIndices
              : null;
    if (!id || !idx || !dmn) return false;
    // Open the decision in table view, then ask the editor for a highlight
    // pass through setSimulationHighlights (same channel the simulator uses).
    try {
      if (typeof dmn.openDecision === 'function') dmn.openDecision(id);
    } catch (_) { /* swallow */ }
    setTimeout(() => {
      try {
        const ed = typeof dmn.getActiveEditor === 'function' ? dmn.getActiveEditor() : null;
        if (ed && typeof ed.setSimulationHighlights === 'function') {
          ed.setSimulationHighlights({ ruleIdxs: idx });
          setTimeout(() => {
            try { ed.setSimulationHighlights(null); } catch (_) {}
          }, 4000);
        }
      } catch (_) { /* non-fatal */ }
    }, 50);
    return true;
  }

  // ── action utilities ────────────────────────────────────────────────────

  _dmnFindNode(dmn, id) {
    if (!dmn || !id) return null;
    if (typeof dmn.getDecisionById === 'function') {
      const d = dmn.getDecisionById(id);
      if (d) return d;
    }
    const defs = typeof dmn.getDefinitions === 'function' ? dmn.getDefinitions() : null;
    if (!defs || !defs.drgElement) return null;
    const direct = defs.drgElement.find((el) => el && el.id === id);
    if (direct) return direct;
    // Search nested information items / requirements.
    for (const drg of defs.drgElement) {
      const reqs = drg.informationRequirement || drg.knowledgeRequirement || drg.authorityRequirement || [];
      const match = reqs.find((r) => r && r.id === id);
      if (match) return match;
    }
    return null;
  }

  _dmnResolveExpression(dmn, exprPath) {
    // exprPath may be the moddle node id for a Literal/Context/etc. We look it
    // up across all decisions; the LLM rarely has the full dotted path.
    if (!exprPath) return null;
    const defs = typeof dmn.getDefinitions === 'function' ? dmn.getDefinitions() : null;
    if (!defs || !defs.drgElement) return null;
    const seen = new Set();
    const visit = (node) => {
      if (!node || typeof node !== 'object' || seen.has(node)) return null;
      seen.add(node);
      if (node.id === exprPath) return node;
      for (const key of Object.keys(node)) {
        const val = node[key];
        if (Array.isArray(val)) {
          for (const v of val) {
            const r = visit(v);
            if (r) return r;
          }
        } else if (val && typeof val === 'object') {
          const r = visit(val);
          if (r) return r;
        }
      }
      return null;
    };
    for (const drg of defs.drgElement) {
      const found = visit(drg);
      if (found) return found;
    }
    return null;
  }

  _dmnSignalChange(dmn) {
    // Trigger the same re-render path the editor uses after structural mutations.
    try {
      if (typeof dmn._rerenderActive === 'function') dmn._rerenderActive();
      if (typeof dmn._revalidate === 'function') dmn._revalidate();
      if (typeof dmn._emit === 'function') dmn._emit('element.changed', { change: 'structuredAction' });
    } catch (_) { /* swallow — re-render is best effort */ }
  }

  // ── Diagram context change ───────────────────────────────────────────────

  /**
   * Called when the active diagram changes (new, load, import).
   * Resets in-flight state and switches Atlas to that diagram's history.
   * @param {string} diagramId
   */
  onDiagramChange(diagramId) {
    this._cancelInFlight();
    this._state        = STATE.IDLE;
    this._pendingDesc  = null;
    this._generatedXml = null;
    this._panel.switchDiagram(diagramId);
  }

  // ── DMN / rules mode ─────────────────────────────────────────────────────

  /**
   * Called when the DMN panel opens (user double-clicked a BusinessRuleTask).
   * Switches Atlas to rules mode: DMN chips, DMN context label, scoped tabs.
   * @param {string} [decisionRef]
   */
  onDmnPanelOpen(decisionRef) {
    this._mode        = 'rules';
    this._decisionRef = decisionRef || null;
    this._panel.setVisualMode('rules');
    // PR 1 §4.6 — swap to per-decisionRef tabs/history bucket. Pass the same
    // ref as decisionName for now; richer decision metadata can be plumbed in
    // later via window.epxDmn.getModel().
    this._panel.switchDmnScope({
      mode: 'rules',
      decisionRef: this._decisionRef,
      decisionName: this._resolveDecisionName(this._decisionRef),
    });
    // PR 1 §4.1 — wire DMN selection / expression-editor signals so chips
    // react to the active node and the current Boxed Expression kind.
    this._attachToDmn(window.epxDmn);
    this._refreshDmnChips();
    this._refreshDmnContextPill();
    // THEMIS_CHAT_PLAN §3.8 — contextual welcome (only on first open of an
    // empty bucket so reopening the same decision doesn't spam the chat).
    this._maybeWelcomeThemis();
  }

  /**
   * Called when the DMN panel closes.
   * Restores Atlas to BPMN mode with the previous selection context.
   */
  onDmnPanelClose() {
    this._mode        = 'bpmn';
    this._decisionRef = null;
    this._dmnSelection = [];
    this._dmnExprKind  = null;
    // THEMIS_CHAT_PLAN §3.3 — release DMN listeners so they don't fire on
    // the next session against a stale epxDmn instance.
    this._detachFromDmn();
    this._panel.switchDmnScope({ mode: 'bpmn' });
    this._applySelectionMode();
  }

  /**
   * Lazy-mount and open the DMN "Generar reglas" wizard (PR 4 §4.3).
   * Does nothing if the modal markup is missing — keeps tests / older builds
   * from crashing.
   */
  _openDmnGenerateModal() {
    if (!document.getElementById('ai-dmn-gen-modal')) {
      this._panel.addErrorBubble('El asistente de generación DMN no está disponible en este build.', null);
      return;
    }
    if (!this._dmnGenerateModal) {
      this._dmnGenerateModal = new DmnGenerateModal();
      this._dmnGenerateModal.mount(null, {
        panel:    this._panel,
        epxDmn:   window.epxDmn,
        language: 'es',
        onApply:  ({ scenarios }) => {
          // Echo a short confirmation in chat so the user sees the same
          // feedback the BPMN wizard provides on apply.
          if (Array.isArray(scenarios) && scenarios.length > 0) {
            this._panel.addAiMessage(
              `Se cargaron ${scenarios.length} escenario(s) en el simulador.`,
            );
          }
        },
      });
    } else {
      // Refresh the epxDmn reference — the user could have closed and
      // reopened the panel against a different decision since last time.
      this._dmnGenerateModal._epxDmn = window.epxDmn;
    }
    this._dmnGenerateModal.open();
  }

  /**
   * Best-effort lookup of the decision's display name from the active DMN
   * model. Falls back to the ref id.
   */
  _resolveDecisionName(decisionRef) {
    if (!decisionRef) return null;
    try {
      const model = window.epxDmn?.getModel?.();
      const drg = model?.drgElements?.find((el) => el.id === decisionRef);
      return drg?.name || decisionRef;
    } catch { return decisionRef; }
  }

  /**
   * PR 1 §4.1 — bind DMN context signals once per epxDmn instance. Idempotent:
   * subsequent calls re-use the existing handlers. Handlers are stored on the
   * instance so {@link _detachFromDmn} can remove them when the panel closes
   * (THEMIS_CHAT_PLAN §3.3 — no listener leaks across open/close cycles).
   */
  _attachToDmn(dmn) {
    if (!dmn || dmn === this._boundDmn) return;
    // If we were bound to a different instance, detach first so we never
    // leak handlers on an old epxDmn (re-init after locale change).
    if (this._boundDmn) this._detachFromDmn();

    this._boundDmn = dmn;
    this._dmnSelection = [];
    this._dmnExprKind  = null;

    if (typeof dmn.on === 'function') {
      this._onDmnSelection = ({ elements } = {}) => {
        if (this._mode !== 'rules') return;
        this._dmnSelection = Array.isArray(elements) ? elements : [];
        this._refreshDmnChips();
        this._refreshDmnContextPill();
      };
      this._onDmnExprOpened = ({ kind } = {}) => {
        if (this._mode !== 'rules') return;
        this._dmnExprKind = kind || null;
        this._refreshDmnChips();
        this._refreshDmnContextPill();
      };
      this._onDmnViewChanged = () => {
        if (this._mode !== 'rules') return;
        this._refreshDmnChips();
        this._refreshDmnContextPill();
      };
      this._onDmnEdit = () => {
        if (this._mode !== 'rules') return;
        // Edits change the focus (e.g. inline overlay opens, decision logic
        // type swaps). Re-resolve so the pill reflects the new state.
        this._refreshDmnContextPill();
      };
      dmn.on('selection.changed',         this._onDmnSelection);
      dmn.on('expression.editor.opened',  this._onDmnExprOpened);
      dmn.on('view.changed',              this._onDmnViewChanged);
      dmn.on('decisionTable.changed',     this._onDmnEdit);
      dmn.on('expression.changed',        this._onDmnEdit);
      dmn.on('element.changed',           this._onDmnEdit);
    }
  }

  _detachFromDmn() {
    const dmn = this._boundDmn;
    if (dmn && typeof dmn.off === 'function') {
      if (this._onDmnSelection)    dmn.off('selection.changed',        this._onDmnSelection);
      if (this._onDmnExprOpened)   dmn.off('expression.editor.opened', this._onDmnExprOpened);
      if (this._onDmnViewChanged)  dmn.off('view.changed',             this._onDmnViewChanged);
      if (this._onDmnEdit) {
        dmn.off('decisionTable.changed',  this._onDmnEdit);
        dmn.off('expression.changed',     this._onDmnEdit);
        dmn.off('element.changed',        this._onDmnEdit);
      }
    }
    this._boundDmn        = null;
    this._onDmnSelection  = null;
    this._onDmnExprOpened = null;
    this._onDmnViewChanged = null;
    this._onDmnEdit       = null;
  }

  /**
   * Recompute the Themis context pill — same role as `_applySelectionMode`
   * for Atlas. Reflects the active decision name, the current view (drd /
   * table / expression), and any DMN selection inside the DRD so the user
   * can see what context Themis will use for the next prompt.
   */
  _refreshDmnContextPill() {
    const dmn = window.epxDmn;
    const view = (dmn && typeof dmn.getView === 'function') ? dmn.getView() : 'drd';
    const selection = (dmn && typeof dmn.getSelection === 'function')
      ? dmn.getSelection()
      : (this._dmnSelection || []);
    this._panel.setRulesContext({
      decisionRef:  this._decisionRef,
      decisionName: this._resolveDecisionName(this._decisionRef),
      view,
      selection: selection || [],
    });
  }

  /**
   * Recompute the DMN chip strip from the latest view + selection + active
   * expression kind. Pulls fresh data from `epxDmn` so it stays consistent
   * even when no event has fired yet (e.g. first paint after panel open).
   */
  _refreshDmnChips() {
    const dmn = window.epxDmn;
    const view = (dmn && typeof dmn.getView === 'function') ? dmn.getView() : 'drd';
    const selection = (dmn && typeof dmn.getSelection === 'function')
      ? dmn.getSelection()
      : (this._dmnSelection || []);
    const dmnContext = { view, exprKind: this._dmnExprKind || undefined };
    this._panel.setChips(
      getChips(selection || [], 'rules', dmnContext),
      (chip) => this._onChip(chip),
    );
  }

  /**
   * THEMIS_CHAT_PLAN §3.8 — drop a one-line welcome bubble describing the
   * decision the user just opened. Only fires when the active history bucket
   * is empty so reopening the same decision doesn't spam the chat.
   */
  _maybeWelcomeThemis() {
    try {
      const history = this._panel.loadHistory();
      if (Array.isArray(history) && history.length > 0) return;
      const dmn = window.epxDmn;
      const ref = this._decisionRef;
      const decision = (ref && dmn && typeof dmn.getDecisionById === 'function')
        ? dmn.getDecisionById(ref)
        : null;
      const name = decision?.name || ref || 'la decisión';
      const dl   = decision?.decisionLogic || null;
      const summary = _summarizeDecisionForWelcome(dl);
      const msg = decision
        ? `Hola, soy Themis. Veo "${name}"${summary ? ` — ${summary}` : ''}. Pregúntame por explicación, cobertura o escenarios.`
        : 'Hola, soy Themis. Abrí el DRD para que te ayude con explicaciones, cobertura y escenarios.';
      this._panel.addAiMessage(msg);
    } catch (_) { /* non-fatal */ }
  }

  // ── Apply suggestion natively via LogicFlow API ──────────────────────────

  _applySuggestion(chipLabel, value) {
    const flow = window.epxFlow;
    const lf   = flow?.getLogicFlow();
    const sel  = this._selection;
    if (!lf || sel.length === 0) return;

    const element = sel[0];

    if (chipLabel === '✏️ Renombrar') {
      lf.setProperties(element.id, { name: value });
      // Task shapes render their own SVG text from properties.name (text.editable=false).
      // Calling updateText on those creates a second overlay — only update for shapes
      // that use the text overlay (events, gateways: text.editable defaults to true).
      const model = lf.graphModel.getElement(element.id);
      if (model?.text?.editable !== false) {
        lf.updateText(element.id, value);
      }
      return;
    }

    if (chipLabel === '🔄 Cambiar tipo' || chipLabel === '🔀 Cambiar tipo') {
      this._replaceElementType(lf, element, value);
      return;
    }

    if (chipLabel === '➕ Agregar siguiente paso') {
      this._addNextStep(lf, element, value);
      return;
    }

    if (chipLabel === '🔗 Conectar nodos') {
      // value format: "sourceId|targetId"
      const [srcId, tgtId] = value.split('|');
      if (srcId && tgtId) {
        lf.addEdge({ type: 'epx:SequenceFlow', sourceNodeId: srcId, targetNodeId: tgtId });
      }
      return;
    }
  }

  /**
   * Natively connects all selected nodes that are not already connected.
   * Sorts nodes left-to-right by x position, then adds a SequenceFlow for each
   * consecutive unconnected pair. Shows a short summary in the chat panel.
   */
  _connectSelectedNodes() {
    const lf = window.epxFlow?.getLogicFlow();
    const sel = this._selection;
    if (!lf || sel.length < 2) return;

    // Only nodes (skip edges that may be in the selection)
    const nodes = sel.filter(el => !el.type?.includes('Flow') && !el.type?.includes('Association'));
    if (nodes.length < 2) return;

    const { edges } = lf.getGraphData();
    const selIds = new Set(nodes.map(n => n.id));

    // Index existing connections between selected nodes
    const connected = new Set(
      edges
        .filter(e => selIds.has(e.sourceNodeId) && selIds.has(e.targetNodeId))
        .map(e => `${e.sourceNodeId}|${e.targetNodeId}`)
    );

    // Sort left-to-right (x ascending), top-to-bottom as tiebreaker
    const sorted = [...nodes].sort((a, b) => {
      const ma = lf.graphModel.getElement(a.id);
      const mb = lf.graphModel.getElement(b.id);
      const dx = (ma?.x ?? 0) - (mb?.x ?? 0);
      if (Math.abs(dx) > 10) return dx;
      return (ma?.y ?? 0) - (mb?.y ?? 0);
    });

    // Add a SequenceFlow for each consecutive pair not already connected
    let added = 0;
    for (let i = 0; i < sorted.length - 1; i++) {
      const src = sorted[i];
      const tgt = sorted[i + 1];
      const key = `${src.id}|${tgt.id}`;
      if (!connected.has(key)) {
        lf.addEdge({ type: 'epx:SequenceFlow', sourceNodeId: src.id, targetNodeId: tgt.id });
        connected.add(key);
        added++;
      }
    }

    // Show short summary in chat
    this._panel.addUserMessage('🔗 Conectar nodos seleccionados');
    if (added > 0) {
      const chain = sorted
        .map(n => n.properties?.name || n.text?.value || n.type.replace('epx:', ''))
        .join(' → ');
      this._panel.addAiMessage(`✓ ${added} conexión${added > 1 ? 'es' : ''} agregada${added > 1 ? 's' : ''}:\n${chain}`);
    } else {
      this._panel.addAiMessage('Los nodos seleccionados ya están todos conectados.');
    }
  }

  /**
   * Replaces a node's type while preserving its position, ID, and connections.
   * Mirrors the logic in EpxFlow's ReplaceMenu._replaceElement.
   */
  _replaceElementType(lf, element, newType) {
    // Accept both bpmn: and epx: prefixes from the LLM
    const epxType = newType.startsWith('bpmn:')
      ? 'epx:' + newType.slice(5)
      : newType;

    const model = lf.graphModel.getElement(element.id);
    if (!model) return;

    const { edges } = lf.getGraphData();
    const incomingEdges = edges.filter(e => e.targetNodeId === element.id);
    const outgoingEdges = edges.filter(e => e.sourceNodeId === element.id);

    // Preserve name and any trigger already on the element
    const oldProps = model.properties || {};
    const newProps = {};
    const KEEP = ['name', 'guid', 'trigger', 'code', 'url', 'attachedToRef'];
    for (const key of KEEP) {
      if (oldProps[key] !== undefined) newProps[key] = oldProps[key];
    }

    lf.deleteNode(element.id);

    const newNode = lf.addNode({
      id: element.id,
      type: epxType,
      x: model.x,
      y: model.y,
      properties: newProps,
    });
    if (!newNode) return;

    for (const edge of incomingEdges) {
      lf.addEdge({ id: edge.id, type: edge.type, sourceNodeId: edge.sourceNodeId,
        targetNodeId: newNode.id, properties: edge.properties });
    }
    for (const edge of outgoingEdges) {
      lf.addEdge({ id: edge.id, type: edge.type, sourceNodeId: newNode.id,
        targetNodeId: edge.targetNodeId, properties: edge.properties });
    }
  }

  /**
   * Creates a new step node after the selected element and connects them with a SequenceFlow.
   * value may be a plain name string or JSON {"type":"epx:UserTask","name":"..."}.
   */
  _addNextStep(lf, element, value) {
    let stepName, stepType;
    try {
      const parsed = JSON.parse(value);
      stepName = parsed.name || value;
      stepType = (parsed.type || 'epx:UserTask').startsWith('bpmn:')
        ? 'epx:' + (parsed.type).slice(5)
        : (parsed.type || 'epx:UserTask');
    } catch (_) {
      stepName = value;
      stepType = 'epx:UserTask';
    }

    const model = lf.graphModel.getElement(element.id);
    if (!model) return;

    const newX = model.x + (model.width || 120) + 100;
    const newY = model.y;
    const newId = 'ai_node_' + Date.now();

    const newNode = lf.addNode({
      id: newId,
      type: stepType,
      x: newX,
      y: newY,
      properties: { name: stepName },
    });

    if (newNode) {
      const newModel = lf.graphModel.getElement(newId);
      if (newModel?.text?.editable !== false) {
        lf.updateText(newId, stepName);
      }
      lf.addEdge({ type: 'epx:SequenceFlow', sourceNodeId: element.id, targetNodeId: newId });
    }
  }

  // ── Native DMN handlers (PR 3 §4.4) ───────────────────────────────────────
  //
  // Each handler:
  //   1. Reads selection / active editor via the public epxDmn surface.
  //   2. Mutates the model through the matching epxDmn API.
  //   3. Echoes a short confirmation in the chat so the user has feedback.
  //
  // No network calls — these chips are tagged `native: true` in CHIP_INTENTS.

  _dispatchNativeDmnIntent(intent, chipLabel) {
    const dmn = window.epxDmn;
    if (!dmn) {
      this._panel.addUserMessage(chipLabel || intent);
      this._panel.addErrorBubble('El editor DMN no está disponible.', null);
      return;
    }
    const handlers = {
      dmn_add_rule:        () => this._dmnAddRule(),
      dmn_ctx_add_entry:   () => this._dmnContextAddEntry(),
      dmn_list_add:        () => this._dmnListAdd(),
      dmn_rel_add_row:     () => this._dmnRelationAddRow(),
      dmn_rel_add_col:     () => this._dmnRelationAddColumn(),
      // ML decisions — bindings suggestion is also nativo (THEMIS_ML §6.2 / §4.9).
      dmn_ml_suggest_bindings: () => this._dmnMlSuggestBindings(),
    };
    const fn = handlers[intent];
    if (!fn) {
      // Native suggest_options for renombrar etc. — fall back to the BPMN
      // suggestion flow which already exists.
      this._panel.addAiMessage('Esta acción aún no está conectada en este contexto.');
      return;
    }
    try {
      const summary = fn();
      this._panel.addUserMessage(chipLabel || intent);
      if (summary) this._panel.addAiMessage(summary);
    } catch (err) {
      console.error('[AI] native DMN handler failed:', err);
      this._panel.addUserMessage(chipLabel || intent);
      this._panel.addErrorBubble(err && err.message ? err.message : String(err), null);
    }
  }

  /**
   * Returns the active boxed-expression node, or null. Recurses through the
   * decision logic when the host opened a Decision/BKM rather than a raw node.
   */
  _activeExpression() {
    const ed = window.epxDmn?.getActiveEditor?.();
    if (!ed) return null;
    if (typeof ed.getExpression === 'function') return ed.getExpression() || null;
    if (typeof ed.getDecision   === 'function') {
      const d = ed.getDecision();
      return d ? (d.decisionLogic || null) : null;
    }
    return null;
  }

  _dmnAddRule() {
    const dmn = window.epxDmn;
    if (typeof dmn.addRule === 'function') {
      const decId = this._activeDecisionId();
      const rule = dmn.addRule(decId);
      if (!rule) throw new Error('No hay una tabla de decisión activa.');
      return `✓ Regla agregada${decId ? ` (decisión ${decId})` : ''}.`;
    }
    // Fallback: drive the editor directly when the public helper isn't there.
    const ed = dmn.getActiveEditor && dmn.getActiveEditor();
    if (ed && typeof ed._addRule === 'function') {
      ed._addRule();
      return '✓ Regla agregada a la tabla.';
    }
    throw new Error('No hay una tabla de decisión activa.');
  }

  _dmnContextAddEntry() {
    const expr = this._activeExpression();
    if (!expr || expr.$type !== 'dmn:Context') {
      throw new Error('Abrí una expresión Context primero.');
    }
    const ok = window.epxDmn?.appendContextEntry?.(expr);
    if (!ok) throw new Error('No se pudo agregar la entrada.');
    return '✓ Entrada agregada al Context.';
  }

  _dmnListAdd() {
    const expr = this._activeExpression();
    if (!expr || expr.$type !== 'dmn:List') {
      throw new Error('Abrí una expresión List primero.');
    }
    const ok = window.epxDmn?.appendListItem?.(expr);
    if (!ok) throw new Error('No se pudo agregar el ítem.');
    return '✓ Ítem agregado a la lista.';
  }

  _dmnRelationAddRow() {
    const expr = this._activeExpression();
    if (!expr || expr.$type !== 'dmn:Relation') {
      throw new Error('Abrí una expresión Relation primero.');
    }
    const ok = window.epxDmn?.appendRelationRow?.(expr);
    if (!ok) throw new Error('No se pudo agregar la fila.');
    return '✓ Fila agregada a la relación.';
  }

  _dmnRelationAddColumn() {
    const expr = this._activeExpression();
    if (!expr || expr.$type !== 'dmn:Relation') {
      throw new Error('Abrí una expresión Relation primero.');
    }
    const ok = window.epxDmn?.appendRelationColumn?.(expr);
    if (!ok) throw new Error('No se pudo agregar la columna.');
    return '✓ Columna agregada a la relación.';
  }

  _dmnMlSuggestBindings() {
    // Stub for now — full implementation lives in THEMIS_ML §4.9.
    // Surface a friendly message instead of failing silently so users on a
    // build that doesn't yet have the helper still get useful feedback.
    const dmn = window.epxDmn;
    if (typeof dmn?.suggestMlBindings === 'function') {
      const decId = this._activeDecisionId();
      const n = dmn.suggestMlBindings(decId);
      return n
        ? `✓ ${n} binding${n === 1 ? '' : 's'} sugerido${n === 1 ? '' : 's'} desde el DRD.`
        : 'No se encontraron coincidencias para sugerir bindings.';
    }
    return 'La sugerencia automática de bindings ML aún no está disponible en este build.';
  }

  _activeDecisionId() {
    const ed = window.epxDmn?.getActiveEditor?.();
    const d  = ed && typeof ed.getDecision === 'function' ? ed.getDecision() : null;
    return d ? d.id : null;
  }

  // ── Abort in-flight request ──────────────────────────────────────────────

  _cancelInFlight() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }
}
